import { Component, effect, model, signal } from "@angular/core";
import { finalize } from "rxjs";
import { Crop } from "../models/crop.model";
import { Client } from "../models/client.model";
import { DataService } from "../services/api/data/data.service";
import { FieldSelectionUpdateComponent } from "../modals/field-selection-update/field-selection-update.component";
import { ModalService } from "../services/modal/modal.service";
import { AuthService } from "../services/api/auth/auth.service";
import { MapComponent } from "./map/map.component";
import { LoadingService } from "../services/loading/loading";
import { currentDate } from "./map/util/utliWaterYear";
import { FieldFeatureCollection } from "../models/fields.model";

@Component({
  selector: "app-field-claim",
  standalone: true,
  imports: [MapComponent, FieldSelectionUpdateComponent],
  templateUrl: "./field-claim.component.html",
  styleUrl: "./field-claim.component.scss",
  host: {
    class: "flex flex-col h-full overflow-hidden",
  },
})
export class FieldClaimComponent {
  crops?: Crop[];
  cropsNoFallow?: Crop[];
  clients: Client[] = [];
  fields?: FieldFeatureCollection;
  clientId = model<string>("17"); // ! Default to cawelo
  properties?: any;

  isDarkMode = false;
  isUpdateZoomLevel = true;
  private fieldsRequestSeq = 0;
  private readonly defaultMapEtDate = currentDate().format("YYYY-MM-DD");

  constructor(
    private dataService: DataService,
    private modalService: ModalService,
    private authService: AuthService,
    private loadingService: LoadingService,
  ) {
    effect(() => {
      this.onClientChange(this.clientId()); // Change client when clientId changes
    });
  }

  onClientChange(clientId: string) {
    this.isUpdateZoomLevel = true;
    this.clientId.set(clientId);
    this.fetchFields(clientId);
  }

  onCreateUpdateCropRecords(fieldId?: number) {
    this.isUpdateZoomLevel = false;
    // IM-613: patch just the edited field in place instead of re-downloading the
    // whole client collection (two ~9k-field calls). Falls back to a full refetch
    // if we can't patch (no id, fields not loaded yet, or the fetch fails).
    if (fieldId != null && this.fields) {
      this.dataService
        .getFieldFeature(fieldId, this.clientId(), true)
        .subscribe({
          next: (feature) => this.patchFieldFeature(fieldId, feature),
          error: () => {
            if (this.clientId()) this.fetchFields(this.clientId());
          },
        });
      return;
    }
    if (this.clientId()) {
      this.fetchFields(this.clientId());
    }
  }

  /**
   * IM-613: Replace a single field's feature in the in-memory collection and
   * reassign `fields` so the map redraws just that field — no network refetch of
   * the whole client. Falls back to a full refetch if the field can't be patched.
   */
  private patchFieldFeature(fieldId: number, feature: any): void {
    if (!this.fields || !feature) {
      if (this.clientId()) this.fetchFields(this.clientId());
      return;
    }
    const features = [...this.fields.features];
    const idx = features.findIndex(
      (f: any) => Number(f?.properties?.id) === Number(fieldId),
    );
    if (idx >= 0) {
      features[idx] = feature;
    } else {
      features.push(feature);
    }
    // New object reference so the map's ngOnChanges fires and redraws.
    this.fields = { ...this.fields, features };
  }

  private openFieldSelectionUpdateModal(): void {
    queueMicrotask(() => this.modalService.turnFieldSelectionUpdateModalOn());
  }

  selectUpdateModalShow(properties: string) {
    const propertyObj = JSON.parse(properties);
    if (
      this.properties &&
      this.properties?.id === propertyObj.id &&
      this.properties?.owner === propertyObj.owner // need to update after claiming => owner change
    ) {
      this.openFieldSelectionUpdateModal(); // ! RIGHT HERE put break point. It seems the properties is not updating like we expected due to the if condition
    } else {
      this.properties = propertyObj;
      this.openFieldSelectionUpdateModal();
    }
  }

  ngOnInit(): void {
    const token = this.authService.getToken();
    if (!token) return;
    const decode = this.authService.getDecodedToken();
    const clientId: string = decode["signup_client_id"]; // Cawelo as default
    if (clientId) {
      this.clientId.set(clientId);
    }

    // fetch all crops
    this.fetchCrops();
    // fetch all clients
    this.fetchClients();
    // fetch all field boundaries
    // Initialize dark mode based on the current state in localStorage
    this.isDarkMode = localStorage.getItem("darkMode") === JSON.stringify(true);
    // Listen for localStorage changes in this tab
    window.addEventListener("storage", this.onStorageChange.bind(this));
  }

  onStorageChange(event: StorageEvent) {}

  ngOnDestroy() {
    // Remove the event listener when the component is destroyed
    window.removeEventListener("storage", this.onStorageChange.bind(this));
  }

  // Getting all crops by calling dataService
  fetchCrops(): void {
    this.loadingService.start({
      token: "field-claim-crops",
      message: "Loading crops",
    });
    this.dataService
      .getAllCrops()
      .pipe(finalize(() => this.loadingService.stop("field-claim-crops")))
      .subscribe({
        next: (crops: Crop[]) => {
          this.crops = crops;
          this.cropsNoFallow = this.crops.filter(
            (crop) => crop.crop_type !== "Fallow",
          );
        },
        error: (error: any) => {
          console.error("Error fetching crops data", error);
        },
      });
  }

  // Getting all clients by calling dataService
  fetchClients(): void {
    this.loadingService.start({
      token: "field-claim-clients",
      message: "Loading clients",
    });
    this.dataService
      .getAllClients()
      .pipe(finalize(() => this.loadingService.stop("field-claim-clients")))
      .subscribe({
        next: (clients: Client[]) => {
          this.clients = clients;
          // Check if current clientId exists in the clients list
          const currentClientId = this.clientId();
          const clientExists = clients.some(
            (client) => currentClientId === (client.id as any),
          );
          // If not, set to the first client in the list
          if (!clientExists && clients.length > 0) {
            this.clientId.set(clients[0].id.toString(10));
          }
        },
        error: (error: any) => {
          console.error("Error fetching GeoJSON data", error);
        },
      });
  }

  // Getting all field boundaries by calling dataService
  fetchFields(clientId?: string): void {
    clientId = !clientId ? "" : clientId;
    const requestSeq = ++this.fieldsRequestSeq;
    this.loadingService.start({
      token: "field-claim-fields",
      message: "Loading fields",
    });
    this.dataService
      .getAllFieldsForDate(clientId, this.defaultMapEtDate)
      .pipe(finalize(() => this.loadingService.stop("field-claim-fields")))
      .subscribe({
        next: (fields: FieldFeatureCollection) => {
          if (requestSeq !== this.fieldsRequestSeq) return;
          this.fields = fields;
          this.hydrateFieldsHistory(clientId, requestSeq);
        },
        error: (error: any) => {
          console.error("Error fetching GeoJSON data", error);
        },
      });
  }

  private hydrateFieldsHistory(clientId: string, requestSeq: number): void {
    this.dataService.getAllFields(clientId).subscribe({
      next: (fields: FieldFeatureCollection) => {
        if (requestSeq !== this.fieldsRequestSeq) return;
        this.fields = fields;
      },
      error: (error: any) => {
        console.error("Error hydrating GeoJSON ET history", error);
      },
    });
  }
}
